import React, { ReactNode } from "react";

// Card component to display metric with title and optional icon
export const StatCard = ({
  title,
  value,
  icon,
  className = "",
  backgroundColor = "",
  textColor = "",
  borderColor = "",
}: {
  title: string;
  value: string | number;
  icon?: ReactNode;
  className?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}) => {
  return (
    <div
      className={`bg-white rounded-lg shadow p-6 ${backgroundColor} ${borderColor} ${className}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className={`text-sm font-medium text-gray-500 ${textColor}`}>
            {title}
          </p>
          <p
            className={`mt-1 text-3xl font-semibold text-gray-900 ${textColor}`}
          >
            {value}
          </p>
        </div>
        {icon && <div className="text-cpaGold">{icon}</div>}
      </div>
    </div>
  );
};

// Simple line chart component
export const LineChart = ({
  data,
  height = 200,
  highlightColor = "text-cpaGold",
}: {
  data: number[];
  height?: number;
  highlightColor?: string;
}) => {
  const max = Math.max(...data);

  return (
    <div style={{ height: `${height}px` }} className="relative w-full mt-4">
      <div className="absolute inset-0 flex items-end">
        {data.map((value, index) => {
          const heightPercentage = (value / max) * 100;
          return (
            <div key={index} className="flex-1 flex justify-center">
              <div
                className={`w-2/3 ${highlightColor} bg-current rounded-t`}
                style={{ height: `${heightPercentage}%` }}
              ></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Area chart component
export const AreaChart = ({
  series,
  labels,
  height = 300,
}: {
  series: Array<{ name: string; data: number[]; color?: string }>;
  labels: string[];
  height?: number;
}) => {
  // Find the global maximum value across all series for consistent scaling
  const allValues = series.flatMap((s) => s.data);
  const globalMax = Math.max(...allValues);

  // Create Y-axis tick values
  const yAxisTicks = [
    0,
    Math.round(globalMax * 0.25),
    Math.round(globalMax * 0.5),
    Math.round(globalMax * 0.75),
    Math.round(globalMax),
  ];

  return (
    <div className="relative" style={{ height: `${height}px` }}>
      {/* Y-axis */}
      <div className="absolute left-0 top-0 bottom-8 w-16 flex flex-col justify-between text-xs text-gray-500">
        {yAxisTicks.reverse().map((tick, i) => (
          <div key={i} className="flex items-center">
            <span>{tick}</span>
            <div className="w-1 h-px bg-gray-200 ml-1"></div>
          </div>
        ))}
      </div>

      <div className="absolute inset-0 left-16 bottom-8 right-0">
        <svg
          className="w-full h-full"
          preserveAspectRatio="none"
          viewBox="0 0 100 40"
        >
          {/* Grid lines */}
          {[0, 10, 20, 30, 40].map((y) => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="#f0f0f0"
              strokeWidth="0.5"
            />
          ))}

          {series.map((s, seriesIndex) => {
            // Ensure we're using the entire width of the chart
            const points = s.data
              .map((value, index) => {
                // Calculate x as percentage of total width
                const x = (index / (s.data.length - 1)) * 100;
                // Calculate y as percentage of max height
                const y = 40 - (value / globalMax) * 35;
                return `${x},${y}`;
              })
              .join(" ");

            return (
              <g key={seriesIndex}>
                <polyline
                  fill="none"
                  stroke={s.color || "#E1A731"}
                  strokeWidth="1.5"
                  points={points}
                />
                <polygon
                  fill={s.color || "#E1A731"}
                  fillOpacity="0.15"
                  points={`${points} 100,40 0,40`}
                />
              </g>
            );
          })}
        </svg>
      </div>

      {/* X-axis labels */}
      <div className="absolute bottom-0 left-16 right-0 h-8">
        <div className="flex justify-between text-xs text-gray-500 w-full px-1">
          {labels.map((label, index) => (
            <div key={index} className="text-center">
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="absolute top-0 right-0 flex space-x-4">
        {series.map((s, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-3 h-3 mr-1 rounded-full"
              style={{ backgroundColor: s.color || "#E1A731" }}
            ></div>
            <span className="text-xs text-gray-600">{s.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Donut/Pie chart component
export const PieChart = ({
  segments,
  height = 200,
  useLegend = true,
  segmentValueLabel = "",
}: {
  segments: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  height?: number;
  useLegend?: boolean;
  segmentValueLabel?: string;
}) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  let currentAngle = 0;

  return (
    <div className="relative" style={{ height: `${height}px` }}>
      <div className="flex h-full">
        {/* Chart SVG */}
        <div className={useLegend ? "w-1/2 h-full" : "w-full h-full"}>
          <svg className="w-full h-full" viewBox="0 0 42 42">
            <circle
              cx="21"
              cy="21"
              r="15.91549430918954"
              fill="transparent"
              stroke="#f3f4f6"
              strokeWidth="3"
            ></circle>

            {segments.map((segment, i) => {
              const percentage = segment.value / total;
              const angle = percentage * 360;

              const startAngle = currentAngle;
              currentAngle += angle;
              const endAngle = currentAngle;

              const startAngleRad = ((startAngle - 90) * Math.PI) / 180;
              const endAngleRad = ((endAngle - 90) * Math.PI) / 180;

              const x1 = 21 + 15.91549430918954 * Math.cos(startAngleRad);
              const y1 = 21 + 15.91549430918954 * Math.sin(startAngleRad);
              const x2 = 21 + 15.91549430918954 * Math.cos(endAngleRad);
              const y2 = 21 + 15.91549430918954 * Math.sin(endAngleRad);

              const largeArcFlag = angle > 180 ? 1 : 0;

              const pathData = [
                `M 21 21`,
                `L ${x1} ${y1}`,
                `A 15.91549430918954 15.91549430918954 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                `Z`,
              ].join(" ");

              return <path key={i} d={pathData} fill={segment.color} />;
            })}

            <circle cx="21" cy="21" r="10" fill="white"></circle>
          </svg>
        </div>

        {/* Side Legend */}
        {useLegend && (
          <div className="w-1/2 flex flex-col justify-center space-y-3 pl-4">
            {segments.map((segment, index) => (
              <div key={index} className="flex items-center gap-1">
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 mr-2 rounded-full"
                    style={{ backgroundColor: segment.color }}
                  ></div>
                  <span className="text-sm text-gray-700">{segment.name}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  ({segmentValueLabel === "dollars" ? "$" : ``}
                  {segment.value}
                  {segmentValueLabel === "dollars"
                    ? ""
                    : ` ${segmentValueLabel}`}
                  )
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Bar chart component
export const BarChart = ({
  data,
  categories,
  height = 300,
}: {
  data: Array<{ name: string; data: number[]; color?: string }>;
  categories: string[];
  height?: number;
}) => {
  const allValues = data.flatMap((series) => series.data);
  const maxValue = Math.max(...allValues, 1);

  // Create Y-axis tick values for better readability
  const yAxisTicks = [
    0,
    Math.ceil(maxValue * 0.25),
    Math.ceil(maxValue * 0.5),
    Math.ceil(maxValue * 0.75),
    Math.ceil(maxValue),
  ];

  return (
    <div className="relative" style={{ height: `${height}px` }}>
      {/* Y-axis */}
      <div className="absolute left-0 top-0 bottom-8 w-8 flex flex-col justify-between text-xs text-gray-500">
        {yAxisTicks.reverse().map((tick, i) => (
          <div key={i} className="flex items-center">
            <span>{tick}</span>
            <div className="w-1 h-px bg-gray-200 ml-1"></div>
          </div>
        ))}
      </div>

      <div className="absolute inset-0 left-8 bottom-8 right-0">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {yAxisTicks
            .slice()
            .reverse()
            .map((_, i) => (
              <div key={i} className="w-full h-px bg-gray-100"></div>
            ))}
        </div>

        {/* SVG-based chart for more reliable rendering */}
        <div className="w-full h-full">
          <svg
            className="w-full h-full"
            viewBox={`0 0 ${categories.length * 100} 100`}
            preserveAspectRatio="none"
          >
            {categories.map((category, categoryIndex) => {
              // Each category (month) gets its own horizontal position
              const xPosition = categoryIndex * 100;

              return (
                <g key={categoryIndex}>
                  {data.map((series, seriesIndex) => {
                    const value = series.data[categoryIndex] || 0;
                    const scaledHeight = (value / maxValue) * 100;

                    // Calculate bar position
                    // For two series - first bar at x+30, second at x+50
                    const barWidth = 20;
                    const barX = xPosition + (seriesIndex === 0 ? 30 : 50);

                    return (
                      <rect
                        key={`${categoryIndex}-${seriesIndex}`}
                        x={barX}
                        y={100 - scaledHeight}
                        width={barWidth}
                        height={scaledHeight}
                        fill={series.color || "#E1A731"}
                        rx="2"
                        className="transition-all duration-300 hover:opacity-80"
                      >
                        <title>{`${series.name}: ${value}`}</title>
                      </rect>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="absolute bottom-0 left-8 right-0 h-8">
        <div className="flex justify-between text-xs text-gray-500 w-full">
          {categories.map((category, index) => (
            <div key={index} className="text-center">
              {category}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="absolute top-0 right-0 flex space-x-4">
        {data.map((series, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-3 h-3 mr-1 rounded-full"
              style={{ backgroundColor: series.color || "#E1A731" }}
            ></div>
            <span className="text-xs text-gray-600">{series.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Analytics section wrapper
export const AnalyticsSection = ({
  title,
  children,
  className = "",
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={`bg-white rounded-lg shadow p-6 mb-6 ${className}`}>
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
};
